import React, { Fragment, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DataTable,
  Table,
  TableBody,
  TableCell,
  TableExpandedRow,
  TableExpandHeader,
  TableExpandRow,
  TableHead,
  TableHeader,
  TableRow
} from '@carbon/react';

import { ReadonlyProvider } from 'core/readonly';
import { routes } from 'core/routing';
import Dashboard from 'features/dashboard/Dashboard';
import ExportActions from 'features/dashboard/export/ExportActions';
import ExportDashboardCard from 'features/dashboard/export/ExportDashboardCard';
import ExportDashboardWrapper from 'features/dashboard/export/ExportDashboardWrapper';
import Outputs from 'features/outputs/Outputs';
import useDashboardData from 'pages/dashboard/useDashboardData';
import ActionButton from 'shared/components/ActionButton';
import TableExpandText from 'shared/components/data-table/TableExpandText';
import ModifiedValue from 'shared/components/ModifiedValue';
import useCanEditProject from 'shared/projects/useCanEditProject';
import { formatNumeric } from 'utils/format';
import styles from 'theme/features/dashboard/export/export-dashboard-wrapper.module.scss';

const ExportProjectPage = () => {
  const navigate = useNavigate();

  const { projectId } = useParams();

  const { project, outputs } = useDashboardData(projectId);
  const canEditProject = useCanEditProject(project?.details);

  const [isFullExport, setIsFullExport] = useState(true);

  const headers = [
    { key: 'businessUnit', header: 'Group' },
    { key: 'subGroup', header: 'Sub-group' },
    { key: 'headcount', header: 'Headcount' },
    { key: 'bandA', header: 'Band A' },
    { key: 'bandAa', header: 'Band AA' },
    { key: 'attendanceRate', header: 'Attendance Rate' }
  ];

  const tableRows = project?.employeePopulation?.map((x) => ({
    id: x.id.toString(),
    businessUnit: x.businessUnit.name,
    subGroup: x.subGroup.name,
    headcount: formatNumeric(x.headcount),
    bandA: x.bandA,
    bandAa: x.bandAa,
    attendanceRate: `${Math.round((x.attendanceRate ?? x.subGroup.attendanceRate) * 100)}%`,
    isCustomAttendanceRate: x.attendanceRate != null && x.attendanceRate !== x.subGroup.attendanceRate,
    notes: x.notes || ''
  }));

  return (
    <ReadonlyProvider value={!canEditProject}>
      <ExportDashboardWrapper isFullExport={isFullExport} onChangeExport={setIsFullExport}>
        {isFullExport && (
          <>
            <ExportDashboardCard title="Dashboard Summary" withContentPadding>
              <Dashboard projectId={projectId} project={project} outputs={outputs} isExport />
            </ExportDashboardCard>
            <ExportDashboardCard title="Group Population Summary">
              <DataTable
                headers={headers}
                rows={tableRows ?? []}
                size="sm"
                render={({ rows, getTableProps, getHeaderProps, getRowProps }) => (
                  <Table {...getTableProps()}>
                    <TableHead>
                      <TableRow>
                        <TableExpandHeader id="expand" />
                        {headers.map((header) => (
                          <TableHeader {...getHeaderProps({ header })} key={header.key}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows?.map((row, index) => {
                        const notes = project?.employeePopulation?.[index]?.notes;
                        const isCustomAttendanceRate = tableRows?.[index]?.isCustomAttendanceRate ?? false;

                        const cells = row.cells.slice(0, row.cells.length - 1);
                        const attendanceRateCell = row.cells[row.cells.length - 1];

                        const attendanceRateCellNode = (
                          <TableCell key={attendanceRateCell.key}>
                            <ModifiedValue isModified={isCustomAttendanceRate}>
                              {attendanceRateCell.value}
                            </ModifiedValue>
                          </TableCell>
                        );

                        return (
                          <Fragment key={row.id}>
                            {notes ? (
                              <>
                                <TableExpandRow expandHeader="expand" {...getRowProps({ row })} isExpanded>
                                  {cells.map((cell) => (
                                    <TableCell key={cell.id}>{cell.value}</TableCell>
                                  ))}
                                  {attendanceRateCellNode}
                                </TableExpandRow>
                                <TableExpandedRow colSpan={headers.length + 2}>
                                  <TableExpandText>{notes}</TableExpandText>
                                </TableExpandedRow>
                              </>
                            ) : (
                              <TableRow {...getRowProps({ row })} key={row.id}>
                                <TableCell key="empty-expand" />
                                {cells.map((cell) => (
                                  <TableCell key={cell.id}>{cell.value}</TableCell>
                                ))}
                                {attendanceRateCellNode}
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              />
            </ExportDashboardCard>
            <br />
            <br />
          </>
        )}
        <ExportDashboardCard title="Target Spaces">
          <Outputs projectId={projectId} project={project} isProjectLoading={!project} isExport />
        </ExportDashboardCard>
      </ExportDashboardWrapper>
      <ExportActions>
        <ActionButton
          className={styles.toggle}
          size="sm"
          kind="secondary"
          onClick={() => navigate(routes.dashboard(projectId))}
        >
          Back to Dashboard
        </ActionButton>
      </ExportActions>
    </ReadonlyProvider>
  );
};

export default ExportProjectPage;
